import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, MenuItem, Select,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import DownloadIcon from "@mui/icons-material/Download";
import { BarChart } from "@mui/x-charts/BarChart";
import GraphView from "../components/cuitIntel/GraphView";
import {
  arcaLookup, getCollector, getEdge, getGraph, getPayer, getPayments, getStatus, searchIntel,
  type Bucket, type CollectorDetail, type EdgeDetail, type Graph, type GraphEdge, type GraphNode, type IntelStatus,
  type Padron, type PayerDetail, type PaymentRow, type PaymentsResult, type SearchResult,
} from "../services/cuitIntel";
import {
  classifyClient, fmtArs, fmtArsShort, fmtCuit, fmtDate8, fmtInt, fmtMonth, fmtPct, fmtTsAr, mergeGraphs,
  monthRange, monthsBetween, parseNodeId, paymentsCsv,
} from "../lib/cuitGraph";
import { BRAND } from "../styles/theme";

/**
 * Inteligencia por CUIT (reemplaza a "Buscar por CUIT", CP-522).
 *
 * Un solo buscador: CUIT (pagador o cobrador), RFC, id de collector, id de Coelsa,
 * monto o rango (min-max). Grafo pagador ↔ collector de profundidad 2 y ficha
 * lateral del nodo o la arista seleccionada. Los datos salen del índice propio
 * de la lambda cuit_intel (ms), no del core.
 */
type Selection =
  | { kind: "payer"; cuit: string }
  | { kind: "collector"; id: string }
  | { kind: "edge"; cuit: string; collector: string };

const MONO = { fontFamily: "monospace" } as const;

function KV({ data }: { data: Record<string, React.ReactNode> }) {
  return (
    <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "150px 1fr", rowGap: 0.5, m: 0, fontSize: 13 }}>
      {Object.entries(data).filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
        <Box key={k} sx={{ display: "contents" }}>
          <Box component="dt" sx={{ color: "text.secondary" }}>{k}</Box>
          <Box component="dd" sx={{ m: 0, wordBreak: "break-word" }}>{v}</Box>
        </Box>
      ))}
    </Box>
  );
}

function Stat({ label, value, sub }: { label: string; value: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 110 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{sub}</Typography>}
    </Box>
  );
}

function Buckets({ buckets, title = "Distribución de montos" }: { buckets: Bucket[]; title?: string }) {
  const max = Math.max(1, ...buckets.map((b) => b.n));
  if (!buckets.length) return null;
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>{title}</Typography>
      {buckets.map((b) => (
        <Box key={b.i} sx={{ display: "grid", gridTemplateColumns: "76px 1fr 90px", alignItems: "center", gap: 1, fontSize: 12, mb: 0.25 }}>
          <span style={{ color: "#6B7280" }}>{b.label}</span>
          <Box sx={{ height: 10, bgcolor: "#EEF2FF", borderRadius: 1, overflow: "hidden" }}>
            <Box sx={{ width: `${(100 * b.n) / max}%`, height: "100%", bgcolor: BRAND.primary }} />
          </Box>
          <span>{fmtInt(b.n)}{b.sum != null ? ` · ${fmtArsShort(b.sum)}` : ""}</span>
        </Box>
      ))}
    </Box>
  );
}

function Counts({ title, data, mono }: { title: string; data: Record<string, number>; mono?: boolean }) {
  const entries = Object.entries(data ?? {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>{title}</Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {entries.map(([k, n]) => (
          <Chip key={k} size="small" variant="outlined" label={`${k} · ${n}`} sx={mono ? { ...MONO, fontSize: 11 } : { fontSize: 11 }} />
        ))}
      </Stack>
    </Box>
  );
}

function SeriesChart({ data, label = "Monto" }: { data: { month: string; sum: number; n: number }[]; label?: string }) {
  if (data.length < 2) return null;
  return (
    <BarChart height={170} margin={{ top: 10, bottom: 24, left: 60, right: 10 }}
      xAxis={[{ data: data.map((d) => fmtMonth(d.month)), scaleType: "band" }]}
      yAxis={[{ valueFormatter: (v: number) => fmtArsShort(v) }]}
      series={[{ data: data.map((d) => Number(d.sum)), label, color: BRAND.primary,
                 valueFormatter: (v: number | null, ctx: { dataIndex: number }) => `${fmtArs(v)} · ${data[ctx.dataIndex]?.n ?? 0} cobros` }]}
      slotProps={{ legend: { hidden: true } }} />
  );
}

function PadronBox({ cuit, padron, onPadron }: { cuit: string; padron: Padron | null; onPadron: (p: Padron) => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function lookup() {
    setBusy(true); setErr(null);
    try { onPadron(await arcaLookup(cuit)); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, flex: 1 }}>Padrón ARCA</Typography>
        <Button size="small" variant="outlined" onClick={lookup} disabled={busy}
          startIcon={busy ? <CircularProgress size={12} /> : undefined}>
          {padron?.consultado_at ? "Actualizar" : "Consultar en ARCA"}
        </Button>
      </Stack>
      {err && <Alert severity="warning" sx={{ mb: 1, py: 0 }}>{err}</Alert>}
      {padron ? (
        <KV data={{
          "Razón social":   padron.razon_social,
          "Actividad":      padron.actividad_principal,
          "Otras":          padron.actividades && padron.actividades.length > 1
                              ? padron.actividades.slice(1).map((a) => a.descripcion).join(" · ") : null,
          "Condición IVA":  padron.condicion_iva_desc,
          "Domicilio":      [padron.direccion, padron.localidad, padron.cod_postal ? `CP ${padron.cod_postal}` : null, padron.provincia].filter(Boolean).join(", ") || null,
          "Consultado":     padron.consultado_at ? fmtTsAr(padron.consultado_at) : null,
          "Error":          padron.error,
        }} />
      ) : (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Sin consultar. La actividad declarada explica el perfil de pagos.</Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------- Paneles
interface PanelProps {
  range:    { from: string; to: string };
  onCenter: (root: string) => void;
  onSelect: (s: Selection) => void;
  padron:   Padron | null;
  onPadron: (cuit: string, p: Padron) => void;
}

function PayerPanel({ cuit, range, onCenter, onSelect, padron, onPadron }: PanelProps & { cuit: string }) {
  const [d, setD] = useState<PayerDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setD(null); setErr(null);
    getPayer(cuit, range).then((r) => alive && setD(r)).catch((e) => alive && setErr(String(e?.message ?? e)));
    return () => { alive = false; };
  }, [cuit, range]);
  if (err) return <Alert severity="error">{err}</Alert>;
  if (!d) return <CircularProgress size={22} />;
  const n = d.node;
  return (
    <Stack spacing={2}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{n.name || "(sin nombre)"}</Typography>
          {n.is_hub && <Chip size="small" label="HUB" color="warning" />}
          {n.dv_ok === false && <Chip size="small" label="DV inválido" color="error" />}
        </Stack>
        <Typography sx={{ ...MONO, fontSize: 14 }}>{fmtCuit(n.cuit)}</Typography>
        {d.collector_of.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
            {d.collector_of.map((c) => (
              <Chip key={c.id} size="small" color="secondary" variant="outlined" label={`También es collector ${c.id}`}
                onClick={() => onSelect({ kind: "collector", id: c.id })} />
            ))}
          </Stack>
        )}
      </Box>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <Stat label="Cobros" value={fmtInt(n.n)} sub={n.nr ? `${fmtInt(n.nr)} rechazados` : undefined} />
        <Stat label="Total" value={fmtArsShort(n.sum)} sub={fmtArs(n.sum)} />
        <Stat label="Collectors" value={fmtInt(n.n_collectors)} />
        <Stat label="Primera vez" value={n.first_ts ? fmtTsAr(n.first_ts).slice(0, 10) : "—"} />
        <Stat label="Última vez" value={n.last_ts ? fmtTsAr(n.last_ts).slice(0, 10) : "—"} />
      </Stack>
      <SeriesChart data={d.series} />
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>A quién le paga ({fmtMonth(d.from)} – {fmtMonth(d.to)})</Typography>
        <Table size="small">
          <TableHead><TableRow><TableCell>Collector</TableCell><TableCell align="right">Cobros</TableCell><TableCell align="right">Total</TableCell><TableCell>Último</TableCell></TableRow></TableHead>
          <TableBody>
            {d.edges.map((e) => (
              <TableRow key={e.collector} hover sx={{ cursor: "pointer" }} onClick={() => onSelect({ kind: "edge", cuit, collector: e.collector })}>
                <TableCell><b>{e.collector}</b>{e.collector_info.nombre && e.collector_info.nombre !== e.collector ? <span style={{ color: "#6B7280" }}> — {e.collector_info.nombre}</span> : null}</TableCell>
                <TableCell align="right">{fmtInt(e.n)}{e.nr ? <span style={{ color: "#B91C1C" }}> (+{e.nr} rech.)</span> : null}</TableCell>
                <TableCell align="right">{fmtArs(e.sum)}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtTsAr(e.last_ts).slice(0, 10)}</TableCell>
              </TableRow>
            ))}
            {d.edges.length === 0 && <TableRow><TableCell colSpan={4} sx={{ color: "text.secondary" }}>Sin cobros en el rango elegido.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Box>
      <Buckets buckets={n.buckets} />
      <Counts title="Nombres vistos" data={n.names} />
      <Counts title="CBU / CVU de origen" data={n.cbus} mono />
      <Counts title="Bancos" data={n.banks} />
      <Divider />
      <PadronBox cuit={cuit} padron={padron} onPadron={(p) => onPadron(cuit, p)} />
      <Button size="small" startIcon={<CenterFocusStrongIcon />} onClick={() => onCenter(`payer:${cuit}`)}>Centrar el grafo acá</Button>
    </Stack>
  );
}

function CollectorPanel({ id, range, onCenter, onSelect, padron, onPadron }: PanelProps & { id: string }) {
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [d, setD] = useState<CollectorDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [top, setTop] = useState<"sum" | "n">("sum");
  useEffect(() => {
    let alive = true;
    setErr(null);
    getCollector(id, month).then((r) => alive && setD(r)).catch((e) => alive && setErr(String(e?.message ?? e)));
    return () => { alive = false; };
  }, [id, month]);
  useEffect(() => { setMonth(undefined); setD(null); }, [id]);
  if (err) return <Alert severity="error">{err}</Alert>;
  if (!d) return <CircularProgress size={22} />;
  const n = d.node, m = d.month;
  const rows = m ? (top === "sum" ? m.top_sum : m.top_n) : [];
  return (
    <Stack spacing={2}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{n.id}</Typography>
          {n.nombre && n.nombre !== n.id && <Typography sx={{ color: "text.secondary" }}>— {n.nombre}</Typography>}
          <Chip size="small" label={n.activo === false ? "INACTIVO" : "ACTIVO"}
            sx={{ fontWeight: 700, color: n.activo === false ? "#B91C1C" : "#166534", bgcolor: n.activo === false ? "#FEE2E2" : "#DCFCE7" }} />
        </Stack>
        <KV data={{
          "CUIT / RFC": <span style={MONO}>{fmtCuit(n.tax_id)}</span>,
          "Razón social": n.razon_social,
          "Procesador": [n.processor, n.sub_processor ? `sub ${n.sub_processor}` : null].filter(Boolean).join(" · ") || null,
          "Tipo": n.tipo === "pj" ? "Persona jurídica" : n.tipo === "ph" ? "Persona humana" : n.tipo,
          "Cobros indexados": `${fmtInt(n.n)} · ${fmtArs(n.sum)} (${n.first_month ? fmtMonth(n.first_month) : "—"} – ${n.last_month ? fmtMonth(n.last_month) : "—"})`,
        }} />
      </Box>
      <SeriesChart data={d.series} />
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, flex: 1 }}>Perfil del mes</Typography>
        <Select size="small" value={m?.month ?? month ?? ""} onChange={(e) => setMonth(String(e.target.value))} sx={{ minWidth: 130, fontSize: 13 }}>
          {d.available_months.slice().reverse().map((mm) => <MenuItem key={mm} value={mm} sx={{ fontSize: 13 }}>{fmtMonth(mm)}</MenuItem>)}
        </Select>
      </Stack>
      {!m ? (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Todavía no hay perfil calculado para este mes (se calcula cada hora).</Typography>
      ) : (
        <>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Stat label="Cobros" value={fmtInt(m.n)} sub={m.nr ? `${fmtInt(m.nr)} rechazados` : undefined} />
            <Stat label="Total" value={fmtArsShort(m.sum)} sub={fmtArs(m.sum)} />
            <Stat label="Pagadores" value={fmtInt(m.payers)} sub={m.unknown_n ? `${fmtInt(m.unknown_n)} sin CUIT` : undefined} />
            <Stat label="Promedio" value={fmtArsShort(m.avg)} />
            <Stat label="Top 10" value={fmtPct(m.top10_share)} sub="del monto" />
          </Stack>
          <Buckets buckets={m.buckets} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>Cuántas veces paga cada pagador</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {Object.entries(m.payer_freq).map(([k, v]) => <Chip key={k} size="small" variant="outlined" label={`${k} cobros: ${fmtInt(v)} pagadores`} sx={{ fontSize: 11 }} />)}
            </Stack>
          </Box>
          <Box>
            <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, flex: 1 }}>Top pagadores</Typography>
              <ToggleButtonGroup size="small" exclusive value={top} onChange={(_, v) => v && setTop(v)}>
                <ToggleButton value="sum" sx={{ fontSize: 11, py: 0.25 }}>por monto</ToggleButton>
                <ToggleButton value="n" sx={{ fontSize: 11, py: 0.25 }}>por cantidad</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Box sx={{ maxHeight: 320, overflow: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell>Pagador</TableCell><TableCell align="right">Cobros</TableCell><TableCell align="right">Total</TableCell></TableRow></TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.cuit} hover sx={{ cursor: "pointer" }} onClick={() => onSelect({ kind: "edge", cuit: r.cuit, collector: id })}>
                      <TableCell>
                        <div>{r.name || "(sin nombre)"} {r.is_hub && <Chip size="small" label="hub" sx={{ height: 16, fontSize: 10 }} />}</div>
                        <div style={{ ...MONO, fontSize: 11, color: "#6B7280" }}>{fmtCuit(r.cuit)}</div>
                      </TableCell>
                      <TableCell align="right">{fmtInt(r.n)}</TableCell>
                      <TableCell align="right">{fmtArs(r.sum)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </>
      )}
      {n.tax_id && /^\d{11}$/.test(n.tax_id) && (
        <>
          <Divider />
          <PadronBox cuit={n.tax_id} padron={padron} onPadron={(p) => onPadron(n.tax_id!, p)} />
        </>
      )}
      <Stack direction="row" spacing={1}>
        <Button size="small" startIcon={<CenterFocusStrongIcon />} onClick={() => onCenter(`collector:${id}`)}>Centrar el grafo acá</Button>
        {n.tax_id && /^\d{11}$/.test(n.tax_id) && (
          <Button size="small" onClick={() => onSelect({ kind: "payer", cuit: n.tax_id! })}>Ver como pagador</Button>
        )}
      </Stack>
      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>Rango del grafo: {fmtMonth(range.from)} – {fmtMonth(range.to)}. El perfil es por mes calendario.</Typography>
    </Stack>
  );
}

function EdgePanel({ cuit, collector, range, onSelect }: PanelProps & { cuit: string; collector: string }) {
  const [d, setD] = useState<EdgeDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setD(null); setErr(null);
    getEdge(cuit, collector, range).then((r) => alive && setD(r)).catch((e) => alive && setErr(String(e?.message ?? e)));
    return () => { alive = false; };
  }, [cuit, collector, range]);
  if (err) return <Alert severity="error">{err}</Alert>;
  if (!d) return <CircularProgress size={22} />;
  const totalN = d.months.reduce((a, m) => a + m.n, 0), totalS = d.months.reduce((a, m) => a + Number(m.sum), 0);
  const names = d.months.reduce<Record<string, number>>((acc, m) => { for (const [k, v] of Object.entries(m.names)) acc[k] = (acc[k] ?? 0) + v; return acc; }, {});
  return (
    <Stack spacing={2}>
      <Box>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Pagos de</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: 16, cursor: "pointer" }} onClick={() => onSelect({ kind: "payer", cuit })}>
          {Object.keys(names)[0] ?? fmtCuit(cuit)} <span style={{ ...MONO, fontWeight: 400, fontSize: 13 }}>{fmtCuit(cuit)}</span>
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>a</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: 16, cursor: "pointer" }} onClick={() => onSelect({ kind: "collector", id: collector })}>
          {collector}{d.collector.nombre && d.collector.nombre !== collector ? <span style={{ fontWeight: 400, color: "#6B7280" }}> — {d.collector.nombre}</span> : null}
        </Typography>
      </Box>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <Stat label="Cobros" value={fmtInt(totalN)} sub={`${fmtMonth(d.from)} – ${fmtMonth(d.to)}`} />
        <Stat label="Total" value={fmtArsShort(totalS)} sub={fmtArs(totalS)} />
        <Stat label="Rechazados" value={fmtInt(d.months.reduce((a, m) => a + m.nr, 0))} />
        <Stat label="Redondos" value={fmtInt(d.months.reduce((a, m) => a + m.n_round, 0))} sub="múltiplos de $1.000" />
      </Stack>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>Por mes</Typography>
        <Table size="small">
          <TableHead><TableRow><TableCell>Mes</TableCell><TableCell align="right">Cobros</TableCell><TableCell align="right">Total</TableCell><TableCell align="right">Mín / Máx</TableCell><TableCell align="right">Días</TableCell></TableRow></TableHead>
          <TableBody>
            {d.months.map((m) => (
              <TableRow key={m.month}>
                <TableCell>{fmtMonth(m.month)}</TableCell>
                <TableCell align="right">{fmtInt(m.n)}{m.nr ? <span style={{ color: "#B91C1C" }}> (+{m.nr})</span> : null}</TableCell>
                <TableCell align="right">{fmtArs(m.sum)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{fmtArsShort(m.min)} / {fmtArsShort(m.max)}</TableCell>
                <TableCell align="right">{m.active_days}</TableCell>
              </TableRow>
            ))}
            {d.months.length === 0 && <TableRow><TableCell colSpan={5} sx={{ color: "text.secondary" }}>Sin cobros en el rango elegido.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Box>
      {d.months.length > 0 && <Buckets buckets={d.months.flatMap((m) => m.buckets).reduce<Bucket[]>((acc, b) => {
        const prev = acc.find((x) => x.i === b.i);
        if (prev) { prev.n += b.n; prev.sum = (prev.sum ?? 0) + (b.sum ?? 0); } else acc.push({ ...b });
        return acc;
      }, []).sort((a, b) => a.i - b.i)} />}
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>Por día</Typography>
        <Box sx={{ maxHeight: 180, overflow: "auto", fontSize: 12, columnCount: { xs: 2, md: 3 }, columnGap: 2 }}>
          {d.days.map((x) => <div key={x.date}>{fmtDate8(x.date)}: {x.n} · {fmtArsShort(x.sum)}</div>)}
        </Box>
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>Muestra de cobros ({d.samples.length})</Typography>
        <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
          Primeros, últimos y mayores de cada mes. El detalle completo está en Rastreo por ID mientras el core lo conserve.
        </Typography>
        <Box sx={{ maxHeight: 260, overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead><TableRow><TableCell>Fecha</TableCell><TableCell align="right">Monto</TableCell><TableCell>Estado</TableCell><TableCell>Id</TableCell></TableRow></TableHead>
            <TableBody>
              {d.samples.map((s) => (
                <TableRow key={s.id || s.ts}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtTsAr(s.ts)}</TableCell>
                  <TableCell align="right">{fmtArs(s.amt, true)}</TableCell>
                  <TableCell>{s.st}</TableCell>
                  <TableCell sx={{ ...MONO, fontSize: 11 }}>{s.id}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
      <Counts title="Nombres vistos" data={names} />
    </Stack>
  );
}

// ---------------------------------------------------------------- Página
const RANGE_OPTIONS = [3, 6, 12, 24];

export default function CuitIntel() {
  const [q, setQ] = useState("");
  const [months, setMonths] = useState(6);
  const range = useMemo(() => monthRange(months), [months]);
  const [status, setStatus] = useState<IntelStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ sev: "info" | "warning" | "success"; text: React.ReactNode } | null>(null);
  const [search, setSearch] = useState<SearchResult | null>(null);
  const [graph, setGraph] = useState<Graph | null>(null);
  const [graphLoading, setGraphLoading] = useState(false);
  const [rootId, setRootId] = useState<string | null>(null);
  const [sel, setSel] = useState<Selection | null>(null);
  const [padrones, setPadrones] = useState<Record<string, Padron>>({});
  const [payments, setPayments] = useState<PaymentsResult | null>(null);
  const [payQuery, setPayQuery] = useState<{ min: number; max: number } | null>(null);
  const [payCollector, setPayCollector] = useState("");
  const [payCuit, setPayCuit] = useState("");
  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => { getStatus().then(setStatus).catch(() => {}); }, []);

  const loadGraph = useCallback(async (root: string, merge = false) => {
    setGraphLoading(true);
    try {
      const g = await getGraph(root, range);
      setGraph((prev) => (merge ? mergeGraphs(prev, g) : g));
      if (!merge) setRootId(g.root);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setGraphLoading(false);
    }
  }, [range]);

  // Cambio de rango: recargar el grafo con la misma raíz.
  useEffect(() => { if (rootId) void loadGraph(rootId); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [range]);

  async function loadPayments(min: number, max: number, cursor: string | null = null) {
    setPayLoading(true);
    try {
      const r = await getPayments({ min, max, months: monthsBetween(range.from, range.to).slice(-6),
                                    collector: payCollector.trim() || undefined, cuit: payCuit.trim() || undefined, cursor });
      setPayments((prev) => cursor && prev ? { ...r, items: [...prev.items, ...r.items] } : r);
      if (!cursor && r.items.length === 0) setNotice({ sev: "info", text: "No hay cobros con ese monto en el rango elegido." });
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setPayLoading(false);
    }
  }

  async function runSearch() {
    const c = classifyClient(q);
    if (c.tipo === "empty") return;
    setLoading(true); setError(null); setNotice(null); setSearch(null); setPayments(null); setPayQuery(null); setSel(null);
    try {
      if (c.tipo === "amount") {
        setGraph(null); setRootId(null);
        setPayQuery({ min: c.min, max: c.max });
        await loadPayments(c.min, c.max);
        return;
      }
      const r = await searchIntel(c.q);
      setSearch(r);
      if (r.tipo === null) { setNotice({ sev: "warning", text: r.error ?? "No entendí la búsqueda." }); return; }
      if (r.tipo === "cuit" && r.valido === false) {
        setNotice({ sev: "warning", text: <>El dígito verificador de <span style={MONO}>{fmtCuit(r.cuit)}</span> no es correcto. Igual se busca lo indexado.</> });
      }
      if (r.tipo === "coelsa_id") {
        if (!r.found || !r.payment) { setGraph(null); setRootId(null); setNotice({ sev: "info", text: r.message ?? "Id no encontrado." }); return; }
        const p = r.payment;
        setNotice({ sev: "success", text: <>Cobro <span style={MONO}>{p.id}</span>: {fmtArs(p.amount, true)} de {p.name} ({fmtCuit(p.cuit)}) a <b>{p.collector}</b> el {fmtTsAr(p.ts)} · {p.status}</> });
        if (p.cuit && /^\d{11}$/.test(p.cuit)) {
          await loadGraph(`payer:${p.cuit}`);
          setSel(p.collector ? { kind: "edge", cuit: p.cuit, collector: p.collector } : { kind: "payer", cuit: p.cuit });
        }
        return;
      }
      if (r.payer) {
        await loadGraph(`payer:${r.payer.cuit}`);
        setSel({ kind: "payer", cuit: r.payer.cuit });
        if (r.collectors.length) {
          setNotice({ sev: "info", text: <>Este CUIT es pagador <b>y</b> collector ({r.collectors.map((c) => c.id).join(", ")}). Se muestra como pagador; desde la ficha podés verlo como collector.</> });
        }
      } else if (r.collectors.length) {
        const col = r.collectors[0];
        await loadGraph(`collector:${col.id}`);
        setSel({ kind: "collector", id: col.id });
        if (r.collectors.length > 1) {
          setNotice({ sev: "info", text: <>El CUIT tiene {r.collectors.length} collectors: {r.collectors.map((c) => (
            <Chip key={c.id} size="small" label={c.id} sx={{ ml: 0.5 }} onClick={() => { void loadGraph(`collector:${c.id}`); setSel({ kind: "collector", id: c.id }); }} />
          ))}</> });
        }
      } else {
        setGraph(null); setRootId(null);
        setNotice({ sev: "info", text: r.tipo === "collector"
          ? "No hay ningún collector con ese id."
          : <>Ningún cobro indexado tiene este {r.tipo === "rfc" ? "RFC" : "CUIT"} como pagador y ningún collector lo tiene como CUIT.
              {status?.first_date ? <> El índice arranca el {fmtDate8(status.first_date)}.</> : null}</> });
      }
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  }

  function onSelectNode(n: GraphNode) {
    const { type, key } = parseNodeId(n.id);
    setSel(type === "collector" ? { kind: "collector", id: key } : { kind: "payer", cuit: key });
  }
  function onSelectEdge(e: GraphEdge) {
    setSel({ kind: "edge", cuit: parseNodeId(e.source).key, collector: parseNodeId(e.target).key });
  }
  function onExpand(n: GraphNode) { void loadGraph(n.id, true); }
  function centerOn(root: string) { void loadGraph(root); }
  function onPaymentRow(r: PaymentRow) {
    if (/^\d{11}$/.test(r.cuit)) { void loadGraph(`payer:${r.cuit}`); setSel({ kind: "edge", cuit: r.cuit, collector: r.collector }); }
  }
  function exportCsv() {
    if (!payments) return;
    const blob = new Blob([paymentsCsv(payments.items)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `cobros_${payQuery?.min}_${payQuery?.max}_${range.from}-${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const selectedId = sel?.kind === "payer" ? `payer:${sel.cuit}` : sel?.kind === "collector" ? `collector:${sel.id}` : null;
  const selectedEdgeKey = sel?.kind === "edge" ? `payer:${sel.cuit}→collector:${sel.collector}` : null;
  const padronFor = (cuit: string) => padrones[cuit] ?? (search?.tipo === "cuit" && search.cuit === cuit ? search.padron : null);
  const coverage = status && (
    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
      Índice: {status.first_date ? `desde ${fmtDate8(status.first_date)}` : "sin datos"}
      {status.last_date ? ` hasta ${fmtDate8(status.last_date)}` : ""}
      {status.last_tick_at ? ` · actualizado ${fmtTsAr(status.last_tick_at)}` : ""}
      {status.task_running ? ` · corriendo: ${status.task_running}` : ""}
      {status.backfill_cursor ? ` · backfill en ${fmtMonth(status.backfill_cursor)}` : ""}
      {" · el día de hoy está incompleto hasta el cierre"}
    </Typography>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Inteligencia por CUIT</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Quién le paga a quién. Buscá un CUIT (pagador o cobrador), un RFC, un collector, un id de Coelsa, un monto
        (<span style={MONO}>5000</span>) o un rango (<span style={MONO}>9900000-10000000</span>).
      </Typography>

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 1 }}>
        <TextField size="small" label="CUIT · collector · id de Coelsa · monto o rango" value={q}
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()}
          sx={{ width: { xs: "100%", sm: 440 } }} inputProps={{ maxLength: 60, style: MONO }} />
        <Button variant="contained" onClick={runSearch} disabled={loading || !q.trim()}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}>Buscar</Button>
        <ToggleButtonGroup size="small" exclusive value={months} onChange={(_, v) => v && setMonths(v)}>
          {RANGE_OPTIONS.map((m) => <ToggleButton key={m} value={m} sx={{ fontSize: 12, py: 0.5 }}>{m} meses</ToggleButton>)}
        </ToggleButtonGroup>
        {classifyClient(q).tipo === "amount" && (
          <>
            <TextField size="small" label="Collector (opcional)" value={payCollector} onChange={(e) => setPayCollector(e.target.value)} sx={{ width: 170 }} />
            <TextField size="small" label="CUIT pagador (opcional)" value={payCuit} onChange={(e) => setPayCuit(e.target.value)} sx={{ width: 190 }} inputProps={{ style: MONO }} />
          </>
        )}
      </Stack>
      {coverage}

      {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity={notice.sev} sx={{ mt: 1.5 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}

      {payQuery && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 700, flex: 1 }}>
              Cobros de {payQuery.min === payQuery.max ? fmtArs(payQuery.min, true) : `${fmtArs(payQuery.min, true)} a ${fmtArs(payQuery.max, true)}`}
              {payments ? ` · ${fmtInt(payments.items.length)}${payments.truncated ? "+" : ""}` : ""}
              <span style={{ fontWeight: 400, color: "#6B7280", fontSize: 12 }}> ({fmtMonth(range.from)} – {fmtMonth(range.to)}, máx. 6 meses)</span>
            </Typography>
            <Tooltip title="Exportar lo cargado a CSV (Excel)"><span>
              <IconButton size="small" onClick={exportCsv} disabled={!payments?.items.length}><DownloadIcon fontSize="small" /></IconButton>
            </span></Tooltip>
          </Stack>
          <Box sx={{ maxHeight: 360, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow>
                <TableCell>Fecha</TableCell><TableCell align="right">Monto</TableCell><TableCell>Pagador</TableCell><TableCell>Collector</TableCell><TableCell>Estado</TableCell><TableCell>Id</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {(payments?.items ?? []).map((r) => (
                  <TableRow key={r.id} hover sx={{ cursor: "pointer" }} onClick={() => onPaymentRow(r)}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtTsAr(r.ts)}</TableCell>
                    <TableCell align="right">{fmtArs(r.amount, true)}</TableCell>
                    <TableCell>{r.name}<div style={{ ...MONO, fontSize: 11, color: "#6B7280" }}>{fmtCuit(r.cuit)}</div></TableCell>
                    <TableCell>{r.collector}</TableCell>
                    <TableCell>{r.status}</TableCell>
                    <TableCell sx={{ ...MONO, fontSize: 11 }}>{r.id}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          {payments?.cursor && (
            <Button size="small" sx={{ mt: 1 }} disabled={payLoading} onClick={() => loadPayments(payQuery.min, payQuery.max, payments.cursor)}>
              {payLoading ? "Cargando…" : "Cargar más"}
            </Button>
          )}
        </Paper>
      )}

      <Box sx={{ display: "flex", gap: 2, mt: 2, flexDirection: { xs: "column", md: "row" }, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <GraphView graph={graph} rootId={rootId} selectedId={selectedId} selectedEdgeKey={selectedEdgeKey} loading={graphLoading}
            onSelectNode={onSelectNode} onSelectEdge={onSelectEdge} onExpandNode={onExpand} />
          {graph && (
            <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
              {graph.nodes.length} nodos · {graph.edges.length} aristas · {fmtMonth(graph.from)} – {fmtMonth(graph.to)}.
              Pagador → sus collectors → los pagadores principales de cada uno (y al revés desde un collector).
            </Typography>
          )}
        </Box>
        {sel && (
          <Paper variant="outlined" sx={{ width: { xs: "100%", md: 440 }, flexShrink: 0, p: 2, maxHeight: { md: "calc(100vh - 140px)" }, overflow: "auto", position: { md: "sticky" }, top: { md: 72 } }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4, flex: 1 }}>
                {sel.kind === "payer" ? "Pagador" : sel.kind === "collector" ? "Collector" : "Pagador → collector"}
              </Typography>
              <IconButton size="small" onClick={() => setSel(null)}><CloseIcon fontSize="small" /></IconButton>
            </Stack>
            {sel.kind === "payer" && (
              <PayerPanel cuit={sel.cuit} range={range} onCenter={centerOn} onSelect={setSel}
                padron={padronFor(sel.cuit)} onPadron={(c, p) => setPadrones((x) => ({ ...x, [c]: p }))} />
            )}
            {sel.kind === "collector" && (
              <CollectorPanel id={sel.id} range={range} onCenter={centerOn} onSelect={setSel}
                padron={null} onPadron={(c, p) => setPadrones((x) => ({ ...x, [c]: p }))} />
            )}
            {sel.kind === "edge" && (
              <EdgePanel cuit={sel.cuit} collector={sel.collector} range={range} onCenter={centerOn} onSelect={setSel}
                padron={null} onPadron={() => {}} />
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
}
